import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DataProvider } from "./contexts/DataContext";
import LoginPage from "./pages/LoginPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import AdminDashboard from "./pages/AdminDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import SchoolDashboard from "./pages/SchoolDashboard";
import ParentDashboard from "./pages/ParentDashboard";
import TripsPage from "./pages/TripsPage";
import VehiclesPage from "./pages/VehiclesPage";
import IncidentsPage from "./pages/IncidentsPage";
import UsersPage from "./pages/UsersPage";
import SchoolsPage from "./pages/SchoolsPage";
import ParentsPage from "./pages/ParentsPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import NotificationsPage from "./pages/NotificationsPage";
import SetupWizard from "./pages/SetupWizard";
import { Layout } from "./components/Layout";

const roleHome = {
  admin: "/admin",
  driver: "/driver",
  school: "/school",
  parent: "/parent",
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F7FA]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#163D6B] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.password_change_required && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={roleHome[user?.role] || "/login"} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to={user?.password_change_required ? "/change-password" : roleHome[user?.role] || "/admin"} replace /> : <LoginPage />
        }
      />
      <Route path="/forgot-password" element={<PasswordResetPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowedRoles={["admin", "driver", "school", "parent"]}>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="setup-wizard" element={<SetupWizard />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="schools" element={<SchoolsPage />} />
        <Route path="parents" element={<ParentsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route
        path="/driver"
        element={
          <ProtectedRoute allowedRoles={["driver"]}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DriverDashboard />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route
        path="/school"
        element={
          <ProtectedRoute allowedRoles={["school"]}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SchoolDashboard />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route
        path="/parent"
        element={
          <ProtectedRoute allowedRoles={["parent"]}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ParentDashboard />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="/" element={<Navigate to={isAuthenticated ? (user?.password_change_required ? "/change-password" : roleHome[user?.role] || "/admin") : "/login"} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DataProvider>
          <div className="min-h-screen overflow-x-hidden bg-[#F5F7FA]">
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </div>
        </DataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
